import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Plot from "react-plotly.js";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import {
  getPGPEMetricPlotLayout,
  getPGPEParcoordPlotLayout,
  getPGPEScatterPlotLayout,
} from "../../utils/plotting";

export default function OptimizationProgress(props) {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();

  // Create a DataFrame with dummy data
  const [selectedPoints, setSelectedPoints] = useState({});
  const [scatterData, setScatterData] = useState({});
  const [lineData, setLineData] = useState({});
  const [parcoordsData, setParcoordsData] = useState({});
  const [hasClickedReset, setHasClickedReset] = useState(true);
  const [currentSelectedPoint, setCurrentSelectedPoint] = useState(null);

  function updateSelectedPointsInPlots(selectedPoint) {
    setCurrentSelectedPoint(selectedPoint);
    // Helper function to update the selectedpoints attribute in the plots
    Object.keys(scatterData).forEach((k) => {
      // update selectedpoints for all traces
      scatterData[k].selectedpoints = [selectedPoint];
    });

    // Update the selected dimension in the parallel coordinates plot
    const newDimensions = [];
    parcoordsData.dimensions.forEach((dimension) => {
      const upperBound = props.plotData[dimension.label].newest[selectedPoint];
      const lowerBound =
        upperBound -
        0.01 * props.plotData[dimension.label].newest[selectedPoint];
      newDimensions.push({
        ...dimension,
        constraintrange: [lowerBound, upperBound],
      });
    });
    setParcoordsData({ ...parcoordsData, dimensions: newDimensions });
  }

  const handleScatterClick = (key, event) => {
    if (event.points.length > 0) {
      const selectedPoint = event.points[0].x;
      setSelectedPoints({ ...selectedPoints, [key]: selectedPoint });
      updateSelectedPointsInPlots(selectedPoint);
    }
    setHasClickedReset(false);
  };
  const handleClickBestEpoch = () => {
    setSelectedPoints({ ...selectedPoints, metric: props.statsData.bestEpoch });
    updateSelectedPointsInPlots(props.statsData.bestEpoch);
    setHasClickedReset(false);
  };

  const handleClickReset = () => {
    setHasClickedReset(true);
  };

  useEffect(() => {
    if (Object.values(props.plotData).length > 0) {
      const initialScatterData = {};
      const initialbestScatterData = {};
      const initialSelectedPoints = {};
      // create index array of data to be used as initial selectedpoints
      const dataIndex = Array.from(
        { length: props.statsData.numTrials },
        (x, i) => i
      );
      // get keys and sort them alphabetically
      Object.keys(props.plotData)
        .sort()
        .forEach((key) => {
          // set initial selected points depending on previous selection or reset
          if (currentSelectedPoint === null || hasClickedReset) {
            initialSelectedPoints[key] = dataIndex;
          } else {
            initialSelectedPoints[key] = [currentSelectedPoint];
          }

          initialScatterData[key] = {
            x: dataIndex,
            y: props.plotData[key].newest,
            mode: "markers",
            type: "scatter",
            marker: {
              size: 8,
              color: props.plotData.metric.newest,
              colorscale: "Bluered",
            },
            name:
              key === "metric"
                ? t("trainingStatus.Metric")
                : t("trainingStatus.Trial"),
            selectedpoints: initialSelectedPoints[key],
          };

          initialbestScatterData[key] = {
            x: dataIndex,
            y: props.plotData[key].best,
            mode: "lines",
            type: "scatter",
            marker: { size: 8, color: theme.palette.secondary.main },
            name: t("trainingStatus.BestSoFar"),
            selectedpoints: initialSelectedPoints[key],
          };
        });

      setLineData(initialbestScatterData);
      setScatterData(initialScatterData);
      setSelectedPoints(initialSelectedPoints);

      const dimensionKeys = Object.keys(props.plotData);
      const sortedKeysWithoutMetric = dimensionKeys
        .filter((key) => key !== "metric")
        .sort();

      if (dimensionKeys.includes("metric")) {
        sortedKeysWithoutMetric.push("metric");
      }

      const dimensions = sortedKeysWithoutMetric.map((key) => ({
        label: key,
        values: props.plotData[key].newest,
      }));

      const initialParcoordsData = {
        line: {
          color: props.plotData.metric.newest,
          colorscale: "Bluered",
          showscale: true,
        },
        dimensions,
        selected: { line: { opacity: 1.0 } },
        unselected: { line: { opacity: 0.1 } },
        type: "parcoords",
      };

      setParcoordsData(initialParcoordsData);
      console.log("props.plotData", props.plotData);
      console.log("props.statsData", props.statsData);
      console.log("initialScatterData", initialScatterData);
      console.log("initialbestScatterData", initialbestScatterData);
      console.log("initialParcoordsData", initialParcoordsData);
    }
  }, [hasClickedReset, props.plotData]);

  return (
    <Grid
      id="obtimization-progress-container"
      container
      columnSpacing={4}
      sx={{ padding: theme.spacing(4, 10) }}
    >
      {
        // only render if current experiment has fulfilled all required steps and stats are loaded
        Object.values(props.plotData).length > 1 ? (
          <>
            <Grid item xs={12} sx={{ margin: theme.spacing(3, 0) }}>
              <Grid item xs={12}>
                <Grid
                  container
                  columnSpacing={3}
                  rowSpacing={3}
                  sx={{ alignItems: "left" }}
                >
                  <Grid item xs={12} sx={{ padding: theme.spacing(4, 1) }}>
                    <Typography variant="h4" gutterBottom>
                      {t("trainingStatus.OptimizerResults")}
                    </Typography>
                    <TableContainer sx={{ margin: theme.spacing(3, 0) }}>
                      <Table aria-label="table">
                        <TableHead>
                          <TableRow>
                            <TableCell>&nbsp;</TableCell>
                            {Object.keys(props.plotData)
                              .sort()
                              .map((key, index) => (
                                <TableCell
                                  key={`header-idx-${index}`}
                                  align="left"
                                >
                                  {/* Replace underscores with spaces and convert to uppercase */}
                                  {key
                                    .split("_")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                    )
                                    .join(" ")}
                                </TableCell>
                              ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow key="row-mu">
                            <TableCell align="left">
                              {t("trainingStatus.BestEpoch")}:{" "}
                              {props.statsData?.bestEpoch}
                              <br />
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleClickBestEpoch}
                              >
                                {t("trainingStatus.ShowBestEpochButton")}
                              </Button>
                            </TableCell>
                            {Object.keys(props.plotData)
                              .sort()
                              .map((key, index) => (
                                <TableCell
                                  align="left"
                                  key={`row-idx-${index}`}
                                >
                                  {props.plotData[key]?.best.at([
                                    props.statsData?.bestEpoch,
                                  ])}
                                </TableCell>
                              ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12} sx={{ margin: theme.spacing(3, 0) }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleClickReset}
                    >
                      {t("trainingStatus.ResetPlotViewButton")}
                    </Button>
                  </Grid>
                  <Grid item xs={6} key="metric">
                    <Plot
                      data={[scatterData.metric, lineData.metric]}
                      onClick={(event) => handleScatterClick("metric", event)}
                      layout={getPGPEMetricPlotLayout(
                        props.statsData?.numTrials,
                        t
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Plot
                      data={[parcoordsData]}
                      layout={getPGPEParcoordPlotLayout(t)}
                    />
                  </Grid>
                  {Object.keys(props.plotData)
                    .sort()
                    .map((key) =>
                      key !== "metric" ? (
                        <Grid item xs={4} key={key}>
                          <Plot
                            data={[scatterData[key], lineData[key]]}
                            onClick={(event) => handleScatterClick(key, event)}
                            layout={getPGPEScatterPlotLayout(
                              key,
                              props.statsData?.numTrials,
                              t
                            )}
                          />
                        </Grid>
                      ) : (
                        ""
                      )
                    )}
                </Grid>
              </Grid>
            </Grid>
          </>
        ) : (
          ""
        )
      }
    </Grid>
  );
}
